<template>
  <div
    :class="{
      'community-boundary-guidance-readonly': true,
      'community-boundary-guidance-readonly--without-border': props.hideBorder,
    }"
  >
    <!--<h3 class="global-h3 community-boundary-guidance-readonly__main-title">Destination Info and Boundary</h3>-->

    <div v-if="props.showEditButton" class="community-boundary-guidance-readonly__edit-link-wrap">
      <LinkWithIcon tag="router-link" :to="{ name: 'CommunityPageAdmin', params: { pageId: props.communityId }, hash: '#visits' }" color="teal">
        <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
        <span>Edit</span>
      </LinkWithIcon>
    </div>

    <div class="community-boundary-guidance-readonly__content-shell">
      <Component
        :is="hasContent ? TransitionedHeightAuto : 'div'"
        class="community-boundary-guidance-readonly__content-wrap"
        :minHeight="70"
        :isOpened="props.hideExpandCollapse || isExpanded"
        contentVerticalAttachment="top"
        isKeepAlive
        :transitionDuration="150"
      >
        <div class="community-boundary-guidance-readonly__inner">
          <div v-if="creatorDirectionTrimmed" class="community-boundary-guidance-readonly__guidance">
            <h5 class="global-h5 community-boundary-guidance-readonly__title">Destination Info</h5>
            <div class="community-boundary-guidance-readonly__text" v-html="linkifyStr(creatorDirectionTrimmed, globalRoot.linkifyOptions)?.trim()"></div>
          </div>

          <div v-if="boundaryData && boundaryData.boundarySelectMode" class="community-boundary-guidance-readonly__boundary">
            <div class="community-boundary-guidance-readonly__boundary-header">
              <h5 class="global-h5 community-boundary-guidance-readonly__title">Destination Boundary</h5>
              <!--<div class="community-boundary-guidance-readonly__description">This is the destination boundary. We recommend verifying any stops outside this boundary with the destination.</div>-->
            </div>
            <DrawingMap
              class="community-boundary-guidance-readonly__map"
              :lat="boundaryData.latitude"
              :lng="boundaryData.longitude"
              :inputGeoJson="boundaryData.boundaryGeoJson"
              :circleRadiusInMiles="+(boundaryData.withinMiles ?? 0)"
              :boundarySelectMode="boundaryData.boundarySelectMode"
              :onlyAllowDrawingMode="true"
              :showBoundaryAsReadonly="true"
              :isCreatorFlow="false"
              mapAspectRatio="2/1"
            />
          </div>

          <div v-else-if="isLoading" class="community-boundary-guidance-readonly__placeholder">
            <div class="community-boundary-guidance-readonly__placeholder-text">Loading map<EllipsisAnimated /></div>
            <img class="community-boundary-guidance-readonly__placeholder-bg" src="/images/map-placeholder.gif" alt="Map" />
          </div>

          <div v-else-if="boundaryData" class="community-boundary-guidance-readonly__empty">No boundary or guidance set for this destination.</div>
        </div>
      </Component>
    </div>

    <div v-if="!props.hideExpandCollapse && !isExpanded && hasContent" class="community-boundary-guidance-readonly__curtain">
      <LinkWithIcon class="community-boundary-guidance-readonly__show-more-btn" tag="div" isDottedUnderline iconInset="1px auto auto 2px" @click="isExpanded = !isExpanded">
        <template #icon><IconEmbedded name="caret-bottom_4" :size="14" /></template>
        <span>Show more</span>
      </LinkWithIcon>
    </div>

    <LinkWithIcon
      v-if="!props.hideExpandCollapse && isExpanded && hasContent"
      class="community-boundary-guidance-readonly__show-more-btn"
      style="margin-top: 8px"
      tag="div"
      isDottedUnderline
      iconInset="1px auto auto 2px"
      @click="isExpanded = false"
    >
      <template #icon><IconEmbedded name="caret-top_4" :size="14" /></template>
      <span>Hide</span>
    </LinkWithIcon>
  </div>
</template>

<script setup lang="ts">
import axios from "axios";
import { inject, ref, watch, computed } from "vue";
import linkifyStr from "linkify-string";

import DrawingMap from "@views/Community/DrawingMap.vue";
import EllipsisAnimated from "@components/ui/EllipsisAnimated.vue";
import TransitionedHeightAuto from "@components/ui/TransitionedHeightAuto.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

import type { CommunityBoundaryResponse } from "@contracts/pages";

const props = withDefaults(
  defineProps<{
    communityId: string;
    hideExpandCollapse?: boolean;
    hideBorder?: boolean;
    showEditButton?: boolean;
  }>(),
  { hideExpandCollapse: false, hideBorder: false, showEditButton: false }
);

const globalRoot = inject("globalRoot") as { linkifyOptions: Record<string, unknown> };

const boundaryData = ref<CommunityBoundaryResponse | null>(null);
const isLoading = ref(false);
const isExpanded = ref(false);

const creatorDirectionTrimmed = ref<string | null>(null);

const hasContent = computed(() => !!(creatorDirectionTrimmed.value || (boundaryData.value && boundaryData.value.boundarySelectMode) || isLoading.value));

async function loadBoundaryData(communityId: string) {
  if (!communityId) return;
  isLoading.value = true;
  const uri = `${import.meta.env.VITE_API_URL}/pages/${communityId}/boundary?includeCreatorDirection=true`;
  try {
    const { data } = await axios.get<CommunityBoundaryResponse>(uri);
    boundaryData.value = data;
    const trimmed = data.creatorDirection?.trim();
    creatorDirectionTrimmed.value = trimmed || null;
  } catch {
    boundaryData.value = null;
    creatorDirectionTrimmed.value = null;
  }
  isLoading.value = false;
}

watch(
  () => props.communityId,
  id => loadBoundaryData(id ?? ""),
  { immediate: true }
);
</script>

<style scoped lang="scss">
.community-boundary-guidance-readonly {
  padding: 21px 25px 24px;
  border-radius: 8px;
  background: white;
  outline: 2px rgba(0, 0, 0, 0.1) solid;
  outline-offset: -2px;
  position: relative;
  z-index: 0;

  &__main-title {
    margin-bottom: 16px;
  }

  &__edit-link-wrap {
    position: absolute;
    inset: 0 0 auto auto;
    z-index: 3;
  }

  &__content-wrap {
    position: relative;
    z-index: 1;
  }

  &__content-shell {
    position: relative;
    z-index: 1;
  }

  &__curtain {
    width: calc(100% - 4px);
    height: 50px;
    position: absolute;
    bottom: 7px;
    left: 2px;
    z-index: 2;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.85) 50%, rgba(255, 255, 255, 1) 100%);
    border-radius: 0 0 6px 6px;
    display: flex;
    align-items: flex-end;
    padding-bottom: 8px;
    justify-content: flex-start;
    padding-left: 25px;
    box-sizing: border-box;
    pointer-events: auto;
  }

  &__guidance {
    margin-bottom: 17px;
  }

  &__title {
    margin-bottom: 8px;
  }

  &__text {
    font: 14px/18px sans-serif;
    color: rgba(91, 91, 91, 1);
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__boundary {
    margin-top: 0;
  }

  &__boundary-header {
    margin-bottom: 0px;
  }

  &__description {
    font: 14px/18px sans-serif;
    color: rgba(91, 91, 91, 1);
  }

  &__map {
    width: 100%;
    border-radius: 8px;
    overflow: hidden;
    margin-top: 0px;
  }

  &__placeholder {
    height: 220px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 0;
    overflow: hidden;
    font-weight: bold;
    text-align: center;
    background: white;
    border-radius: 8px;
  }

  &__placeholder-text {
    text-align: center;
  }

  &__placeholder-bg {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: -1;
    opacity: 0.7;
  }

  &__show-more-btn {
    position: relative;
    z-index: 3;
  }

  &__empty {
    font: 14px/18px sans-serif;
    color: rgba(91, 91, 91, 1);
  }

  &--without-border {
    padding: 0;
    outline: none;
    outline-offset: 0;
  }
}
</style>
